// Frontend wrappers for /api/insurance/* serverless functions
import { apiFetch } from './apiFetch';

export async function fetchPolicies(filters = {}) {
  const params = new URLSearchParams(filters);
  const res = await apiFetch(`/api/insurance/policies?${params}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch policies');
  return data.policies || [];
}

export async function fetchPolicy(id) {
  const res = await apiFetch(`/api/insurance/policies/${id}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch policy');
  return data.policy;
}

export async function savePolicy(policy) {
  const method = policy.id ? 'PUT' : 'POST';
  const url = policy.id ? `/api/insurance/policies/${policy.id}` : '/api/insurance/policies';
  const res = await apiFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(policy),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save policy');
  return data.policy;
}

export async function fetchRenewalsUpcoming(daysAhead = 90) {
  const res = await apiFetch(`/api/insurance/renewals?days_ahead=${daysAhead}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch renewals');
  return data.renewals || [];
}

export async function fetchCarriers() {
  const res = await apiFetch('/api/insurance/carriers');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch carriers');
  return data.carriers || [];
}

export async function deletePolicy(id) {
  const res = await apiFetch(`/api/insurance/policies/${id}`, { method: 'DELETE' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete policy');
  return data;
}

export async function getDocumentUrl(documentId) {
  const res = await apiFetch(`/api/insurance/document/${documentId}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to get document URL');
  return data.signedUrl;
}

export async function fetchDashboard() {
  const res = await apiFetch('/api/insurance/dashboard');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch dashboard');
  return data;
}

// ─── Audit log ─────────────────────────────────────────────────────

export async function fetchAuditLog({ table, recordId, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (table) params.set('table', table);
  if (recordId) params.set('record_id', recordId);
  if (limit) params.set('limit', String(limit));
  const res = await apiFetch(`/api/data/audit?${params}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch audit log');
  return data.entries || [];
}

// ─── Document requests ────────────────────────────────────────────

export async function fetchRequests(filters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.household_id) params.set('household_id', filters.household_id);
  const qs = params.toString();
  const res = await apiFetch(`/api/insurance/requests${qs ? '?' + qs : ''}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch requests');
  return data.requests || [];
}

export async function createRequest(payload) {
  const res = await apiFetch('/api/insurance/requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create request');
  return data.request;
}

export async function updateRequest(id, patch) {
  const res = await apiFetch(`/api/insurance/requests/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update request');
  return data.request;
}

export async function deleteRequest(id) {
  const res = await apiFetch(`/api/insurance/requests/${id}`, { method: 'DELETE' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete request');
  return data;
}

export async function fetchSettings() {
  const res = await apiFetch('/api/insurance/settings');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch settings');
  return data.settings || {};
}

export async function saveSettings(settings) {
  const res = await apiFetch('/api/insurance/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save settings');
  return data.settings;
}

export async function reExtractPolicy(policyId) {
  const res = await apiFetch('/api/insurance/re-extract', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policyId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to re-extract');
  return data;
}

export async function enrichHome(policyId) {
  const res = await apiFetch('/api/insurance/enrich-home', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policyId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to enrich home');
  return data;
}
